import React, { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { getSaasApiBaseUrl } from '../../utils/saasApiBaseUrl';

function formatApiError(payload) {
  const d = payload?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(' ');
  }
  return payload?.message || 'Request failed';
}

function formatAddress(loc) {
  const line1 = [loc.address, loc.address_line2].filter(Boolean).join(', ');
  const cityState = [loc.city, loc.state].filter(Boolean).join(', ');
  const line2 = [cityState, loc.postal_code].filter(Boolean).join(' ');
  return [line1, line2].filter(Boolean).join(', ');
}

/**
 * Read-only list of practice locations on file for this organization.
 * Source of truth: SaaSFoundation organization_locations. View-only by design —
 * no edit/delete affordances are wired here even though the API supports them.
 */
export default function LocationsSection({ organization, onMessage }) {
  const organizationId = organization?.id || organization?.organization_id;
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!organizationId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const API_BASE_URL = getSaasApiBaseUrl();
        const res = await fetch(`${API_BASE_URL}/organizations/current/locations`, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(formatApiError(err) || `Request failed (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) setLocations(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          const text = err.message || 'Failed to load locations.';
          setError(text);
          onMessage?.({ type: 'error', text });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return (
    <div className="mt-8 pt-8 border-t border-gray-200 space-y-4">
      <div className="flex items-center gap-2">
        <MapPin className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-medium text-gray-900">Locations</h3>
      </div>
      <p className="text-sm text-gray-500 max-w-2xl">
        Practice locations on file for this organization. Contact your account admin to add or change locations.
      </p>

      {loading && <div className="text-sm text-gray-500">Loading locations...</div>}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
      )}

      {!loading && !error && locations.length === 0 && (
        <div className="text-sm text-gray-500">No locations on file yet.</div>
      )}

      {!loading && !error && locations.length > 0 && (
        <div className="space-y-2">
          {locations.map((loc) => (
            <div key={loc.id} className="p-3 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-900">{loc.name}</span>
                <div className="flex items-center gap-2">
                  {loc.is_primary && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Primary
                    </span>
                  )}
                  {loc.is_active === false && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      Inactive
                    </span>
                  )}
                </div>
              </div>
              {formatAddress(loc) && (
                <p className="text-sm text-gray-600 mt-1">{formatAddress(loc)}</p>
              )}
              {(loc.phone || loc.npi) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                  {loc.phone && <span>Phone: {loc.phone}</span>}
                  {loc.npi && <span>NPI: {loc.npi}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
